// Axial hex-grid math (pointy-top layout). See doc/game_design.md — the map
// is discretized into hexes; nodes sit on hexes, lines run along hex edges.

export interface Axial {
  q: number;
  r: number;
}

const DIRECTIONS: Axial[] = [
  { q: 1, r: 0 },
  { q: 1, r: -1 },
  { q: 0, r: -1 },
  { q: -1, r: 0 },
  { q: -1, r: 1 },
  { q: 0, r: 1 },
];

export function hexKey(a: Axial): string {
  return `${a.q},${a.r}`;
}

export function parseHexKey(key: string): Axial {
  const [q, r] = key.split(",").map(Number);
  return { q, r };
}

/** All hexes within `radius` of the origin — a hexagon-shaped map. */
export function hexesInRadius(radius: number): Axial[] {
  const result: Axial[] = [];
  for (let q = -radius; q <= radius; q++) {
    const rMin = Math.max(-radius, -q - radius);
    const rMax = Math.min(radius, -q + radius);
    for (let r = rMin; r <= rMax; r++) {
      result.push({ q, r });
    }
  }
  return result;
}

export function neighbors(a: Axial): Axial[] {
  return DIRECTIONS.map((d) => ({ q: a.q + d.q, r: a.r + d.r }));
}

export function areAdjacent(a: Axial, b: Axial): boolean {
  return DIRECTIONS.some((d) => a.q + d.q === b.q && a.r + d.r === b.r);
}

// Parallel to DIRECTIONS above — index-for-index, derived from each
// direction's actual on-screen offset in the pointy-top layout (see
// axialToPixel/hexCorners in render.ts): {1,0} is straight right (E),
// {1,-1}/{0,-1} are the two upper neighbors (NE/NW), and so on around.
const DIRECTION_LABELS = ["E", "NE", "NW", "W", "SW", "SE"];

/** Compass label for the direction from `a` to `b`, if they're adjacent —
 * "?" otherwise (shouldn't happen for two hexes that are actually
 * neighbors, but callers may pass unrelated hexes by mistake). */
export function directionLabel(a: Axial, b: Axial): string {
  const dq = b.q - a.q;
  const dr = b.r - a.r;
  const index = DIRECTIONS.findIndex((d) => d.q === dq && d.r === dr);
  return index >= 0 ? DIRECTION_LABELS[index] : "?";
}

/** Canonical key for the edge between two adjacent hexes (order-independent). */
export function edgeKey(a: Axial, b: Axial): string {
  const ka = hexKey(a);
  const kb = hexKey(b);
  return ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
}

export interface Point {
  x: number;
  y: number;
}

export function axialToPixel(a: Axial, size: number): Point {
  const x = size * (Math.sqrt(3) * a.q + (Math.sqrt(3) / 2) * a.r);
  const y = size * (1.5 * a.r);
  return { x, y };
}

export function pixelToAxial(p: Point, size: number): Axial {
  const q = ((Math.sqrt(3) / 3) * p.x - (1 / 3) * p.y) / size;
  const r = ((2 / 3) * p.y) / size;
  return axialRound(q, r);
}

function axialRound(qf: number, rf: number): Axial {
  const sf = -qf - rf;
  let q = Math.round(qf);
  let r = Math.round(rf);
  let s = Math.round(sf);

  const qDiff = Math.abs(q - qf);
  const rDiff = Math.abs(r - rf);
  const sDiff = Math.abs(s - sf);

  if (qDiff > rDiff && qDiff > sDiff) {
    q = -r - s;
  } else if (rDiff > sDiff) {
    r = -q - s;
  }
  return { q, r };
}

export function hexCorners(center: Point, size: number): Point[] {
  const corners: Point[] = [];
  for (let i = 0; i < 6; i++) {
    // pointy-top: first corner offset by -30deg
    const angle = (Math.PI / 180) * (60 * i - 30);
    corners.push({ x: center.x + size * Math.cos(angle), y: center.y + size * Math.sin(angle) });
  }
  return corners;
}
